import {ThreeAnnotationBase} from "@app/template-editor/helpers/three-annotation-base"
import {SceneNodes, SceneNodePart} from "@cm/template-nodes"
import {anyDifference, objectFieldsDifferent} from "@template-editor/helpers/change-detection"
import {ThreeObject, mathIsEqual, setThreeObjectPart, updateTransform} from "@template-editor/helpers/three-object"
import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {AnnotationStyleRegistry} from "@app/template-editor/helpers/annotation-style-registry"

const css = `
.cm-annotation-label {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 22px;
    height: 22px;
    background-color: #00000080;
    box-shadow: 0 0 0 2px #ffffff80;
    font-size: 14px;
    font-weight: bold;
    color: white;
    border-radius: 50%;
}
.cm-annotation-container {
    position: absolute;
    cursor: pointer;
    user-select: none;
    pointer-events: auto;
}
.cm-annotation-container:hover .cm-annotation-label {
    color: #40c4ff;
    box-shadow: 0 0 0 2px #40c4ff;
}`

AnnotationStyleRegistry.getInstance().registerStyles("annotation-styles", css)

export class ThreeAnnotationObject extends ThreeAnnotationBase {
    private readonly labelContainerElement: HTMLDivElement
    private readonly labelElement: HTMLDivElement
    private readonly descriptionElem: HTMLDivElement

    constructor(
        threeSceneManagerService: ThreeSceneManagerService,
        sceneNodePart: SceneNodePart,
        private readonly concealable: boolean,
    ) {
        super(threeSceneManagerService, sceneNodePart)

        this.labelElement = document.createElement("div")
        this.labelElement.className = "cm-annotation-label"
        this.descriptionElem = document.createElement("div")

        this.labelContainerElement = document.createElement("div")
        this.labelContainerElement.className = "cm-annotation-container"
        this.labelContainerElement.appendChild(this.labelElement)

        this.init()
    }

    protected updateStyle(highlighted: boolean, selected: boolean): void {
        const backgroundColor = highlighted ? "red" : ""
        const pointerEvents = selected ? "none" : "auto"

        if (this.labelElement.style.backgroundColor !== backgroundColor || this.labelContainerElement.style.pointerEvents !== pointerEvents) {
            this.labelElement.style.backgroundColor = backgroundColor
            this.labelContainerElement.style.pointerEvents = pointerEvents
            this.dispose(false)
            this.threeSceneManagerService.redraw()
        }
    }

    isConcealable() {
        return this.concealable
    }

    updateLabels(label: string, description: string) {
        // Use textContent so that user-controlled label/description values from template scene
        // graph data cannot inject HTML (these annotations bypass Angular's sanitizer).
        this.labelElement.textContent = label
        this.descriptionElem.textContent = description
        this.dispose(false)
    }

    protected getContainer(): HTMLDivElement {
        return this.labelContainerElement
    }

    getTranslationOffset(element: HTMLDivElement) {
        return {x: -element.clientWidth / 2, y: -element.clientHeight / 2}
    }

    getOcclusionOpacity(): number {
        return 0.2
    }
}

export class ThreeAnnotation extends ThreeObject<SceneNodes.Annotation> {
    protected override renderObject: ThreeAnnotationObject | undefined = undefined

    override setup(sceneNode: SceneNodes.Annotation) {
        if (this.renderObject === undefined) {
            this.renderObject = new ThreeAnnotationObject(this.threeSceneManagerService, {sceneNode, part: "root"}, true)
            setThreeObjectPart(this.renderObject, this)
        } else this.renderObject.updateSceneNode(sceneNode)

        const renderObject = this.renderObject

        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => {
                    return mathIsEqual(valueA, valueB)
                },
                ({transform}) => {
                    updateTransform(transform, renderObject)
                },
            ),
            objectFieldsDifferent(sceneNode, this.parameters, ["label", "description"], undefined, ({label, description}) => {
                renderObject.updateLabels(label, description)
            }),
        ])
    }

    override dispose() {
        if (this.renderObject) this.renderObject.dispose(true)
    }
}
